/** @file Hierarchy row actions: selection, focus, rename, delete targets, menu items and what they do. */
import type { CommandCall, Doc, Id } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { deletable } from '../../forms/deletable';
import type { DeleteTarget } from '../../forms/deletePlan';
import { freeName } from '../../forms/freeName';
import type { RowAction } from '../../forms/rowAction';
import type { InspectorFocus } from '../../stores/focusStore';
import type { SelectionSlice } from '../../stores/types';
import type { MenuItem } from '../../ui-kit/ContextMenuList';
import type { HierarchyRow } from './parseRowId';

const userBone = (doc: Doc, id: Id): boolean => doc.bones[id]?.kind === undefined;
const calls = (list: CommandCall[], expand: Id | null = null): RowAction => ({ kind: 'calls', calls: list, expand, focus: null });

/** The ids the Hierarchy click changes: the bone or the slot a row stands for. */
export interface RowPick { bones: Id[]; slots: Id[] }

const ACTIONS: Readonly<Record<string, (doc: Doc, row: HierarchyRow) => RowAction | null>> = {
  newBone: (doc, row) => (row.kind !== 'bone' ? null : calls([{ name: 'bone.create', input: { name: freeName(doc, 'bone', 'bone'), parent: row.id } }], row.id)),
  newSlot: (doc, row) => (row.kind !== 'bone' ? null : calls([{ name: 'slot.create', input: { name: freeName(doc, 'slot', doc.bones[row.id]?.name ?? 'slot'), bone: row.id } }], row.id)),
  rename: () => ({ kind: 'rename' }),
  remove: (doc, row) => (row.kind === 'placeholder' || !deletable(doc, row) ? null : { kind: 'delete', target: { kind: row.kind, id: row.id } }),
  show: (_doc, row) => (row.kind !== 'placeholder' ? null : calls([{ name: 'slot.setAttachment', input: { target: row.slot, value: row.placeholder } }])),
  removeEntry: (_doc, row) => (row.kind !== 'placeholder' ? null : calls([{ name: 'skin.removeEntry', input: { slot: row.slot, placeholder: row.placeholder } }])),
};

/** Context-menu entries of a bone row; generated socket bones and the root disable what the commands refuse. */
function boneItems(doc: Doc, id: Id, t: Translate): MenuItem[] {
  const generated = !userBone(doc, id);
  return [
    { id: 'newBone', label: t('panels.hierarchy.newBone'), disabled: generated },
    { id: 'newSlot', label: t('panels.hierarchy.newSlot') },
    { id: 'rename', label: t('panels.hierarchy.rename'), disabled: generated },
    { id: 'remove', label: t('panels.hierarchy.remove'), danger: true, separatorBefore: true, disabled: !deletable(doc, { kind: 'bone', id }) },
  ];
}

/** Row actions of the Hierarchy panel (pure; the panel applies the results). */
export const hierarchyActions = {
  /** The bone or slot a click on `row` picks (a placeholder row picks its slot); feed it to updateSelection. */
  pick(row: HierarchyRow): RowPick {
    if (row.kind === 'bone') return { bones: [row.id], slots: [] };
    return { bones: [], slots: [row.kind === 'slot' ? row.id : row.slot] };
  },
  /** The Inspector focus of a placeholder row: the default skin's attachment for it; null for other rows. */
  focus(doc: Doc, row: HierarchyRow): InspectorFocus | null {
    if (row.kind !== 'placeholder') return null;
    const entry = doc.skins[doc.defaultSkin]?.entries.find((e) => e.slot === row.slot && e.placeholder === row.placeholder);
    return entry === undefined ? null : { kind: 'attachment', id: entry.attachment };
  },
  /** User bones and slots can be renamed inline. */
  canRename(doc: Doc, row: HierarchyRow): boolean {
    return row.kind === 'slot' || (row.kind === 'bone' && userBone(doc, row.id));
  },
  /** bone.rename or slot.rename for a renamed row (callers check canRename first). */
  renameCall(row: HierarchyRow, name: string): CommandCall {
    const target = row.kind === 'placeholder' ? row.slot : row.id;
    return { name: row.kind === 'bone' ? 'bone.rename' : 'slot.rename', input: { target, name } };
  },
  /** The selected bones, then the selected slots, as delete targets (the Delete key); ids the commands would refuse are left out. */
  deleteTargets(doc: Doc, selection: SelectionSlice): DeleteTarget[] {
    const all = [...selection.bones.map((id): DeleteTarget => ({ kind: 'bone', id })), ...selection.slots.map((id): DeleteTarget => ({ kind: 'slot', id }))];
    return all.filter((target) => deletable(doc, target));
  },
  /** Context-menu items for a row, labels translated. */
  items(doc: Doc, row: HierarchyRow, t: Translate): MenuItem[] {
    if (row.kind === 'bone') return boneItems(doc, row.id, t);
    if (row.kind === 'slot') return [{ id: 'rename', label: t('panels.hierarchy.rename') }, { id: 'remove', label: t('panels.hierarchy.remove'), danger: true, separatorBefore: true }];
    const shown = doc.slots[row.slot]?.setup.attachment === row.placeholder;
    return [{ id: 'show', label: t('panels.hierarchy.show'), disabled: shown }, { id: 'removeEntry', label: t('panels.hierarchy.removeEntry'), danger: true }];
  },
  /** What choosing `item` on `row` does; ids of created entities are minted by the dispatch (W3-11). */
  action(doc: Doc, row: HierarchyRow, item: string): RowAction | null {
    return ACTIONS[item]?.(doc, row) ?? null;
  },
};
